import {hashKey, type InvalidateQueryFilters, type QueryKey, useQueryClient} from '@github-ui/react-query'
import {useAlive} from '@github-ui/use-alive'
import {useCallback} from 'react'
import {useThrottledCallback} from '@tanstack/react-pacer'

/**
 * Minimal shape every alive payload is expected to have. `wait` accounts for replication lag: the publisher asks
 * consumers to hold off for this many milliseconds before reading.
 */
export interface AliveUpdateData {
  wait?: number
}

export interface UseInvalidateOnAliveUpdateConfig<TData extends AliveUpdateData = AliveUpdateData> {
  /** Option filter for events; return `false` to ignore an event. */
  filter?: (data: TData) => boolean
  /** Throttle timeout to limit frequency of invalidations. Defaults to 2s. */
  throttleTimeout?: number
}

const pendingInvalidations = new Set<string>()

const isQueryKey = (queryKeyOrFilters: QueryKey | InvalidateQueryFilters): queryKeyOrFilters is QueryKey =>
  Array.isArray(queryKeyOrFilters)

/**
 * Invalidate a query whenever a matching alive update arrives on a channel. Deduped across multiple updates for the
 * same query key.
 * @param queryKeyOrFilters If a query key is passed (typical usage), will invalidate just this query with exact key matching.
 * For more custom key matching / multiple-query invalidation, a custom filters parameter can be provided.
 */
export function useInvalidateOnAliveUpdate<TData extends AliveUpdateData = AliveUpdateData>(
  channel: string | null | undefined,
  queryKeyOrFilters: QueryKey | InvalidateQueryFilters,
  config?: UseInvalidateOnAliveUpdateConfig<TData>,
) {
  const queryClient = useQueryClient()

  const filter = config?.filter

  const throttledInvalidate = useThrottledCallback(
    (...args: Parameters<typeof queryClient.invalidateQueries>) => queryClient.invalidateQueries(...args),
    {wait: config?.throttleTimeout ?? 2000},
  )

  const handleNotification = useCallback(
    (data: TData) => {
      const shouldHandle = filter?.(data) ?? true
      if (!shouldHandle) return

      const invalidateParams = isQueryKey(queryKeyOrFilters)
        ? {queryKey: queryKeyOrFilters, exact: true}
        : queryKeyOrFilters
      const hashedInvalidateParams = JSON.stringify(invalidateParams, (k, v) => (k === 'queryKey' ? hashKey(v) : v))

      // Deduplicate multiple simultaneous calls (typically from multiple mounts of the same hook). Note: it's tempting
      // to throttle globally instead: this would almost work but would still result in extra calls because two
      // simultaneous throttle calls still results in two calls (one `start` and one `end` call).
      if (pendingInvalidations.has(hashedInvalidateParams)) return
      pendingInvalidations.add(hashedInvalidateParams)

      setTimeout(
        () => {
          pendingInvalidations.delete(hashedInvalidateParams)
          throttledInvalidate(invalidateParams)
        },
        // Add a small delay to ensure simultaneous calls are collected
        (data.wait ?? 0) + 25,
      )
    },
    [filter, queryKeyOrFilters, throttledInvalidate],
  )

  useAlive(channel, handleNotification)
}
